/* Shared, on-demand global Devices search. Collection/hub mini searches stay local. */
(() => {
  'use strict';
  if (window.SlickwrapsDeviceSearch) return;

  const SECTION = 'sw-device-search-data';
  const MAX_PAGES = 100;
  const MAX_COLLECTIONS = 25000;
  const TIMEOUT_MS = 15000;
  let devices = Object.freeze([]);
  let complete = false;
  let pending = null;

  const normalize = value => String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  const aliasesText = device => Array.isArray(device.aliases) ? device.aliases.join(' ') : String(device.aliases || '');
  const releaseYear = device => {
    const year = Number(device.releaseYear);
    return Number.isInteger(year) && year >= 1900 && year <= 2200 ? year : null;
  };
  const publishedAt = device => Number.isSafeInteger(device.publishedAt) &&
    device.publishedAt >= 0 && device.publishedAt <= 253402300799 ? device.publishedAt : null;
  const devicePath = device => String(device.url || '').split('?')[0].replace(/\/$/, '');
  const score = (device, query) => {
    const value = normalize(query);
    if (!value) return 0;
    const title = normalize(device.title), aliases = normalize(aliasesText(device));
    const brand = normalize(device.brand), category = normalize(device.category);
    if (title === value) return 1000;
    if (title.startsWith(value)) return 900;
    if (title.includes(value)) return 800;
    if (aliases === value) return 750;
    if (aliases.includes(value)) return 700;
    if (brand === value) return 600;
    if (brand.includes(value)) return 550;
    if (normalize(device.releaseYear) === value) return 500;
    if (category.includes(value)) return 400;
    return 0;
  };
  const compare = (a, b, query = '', sort = '') => {
    const scoreDifference = score(b, query) - score(a, query);
    if (scoreDifference) return scoreDifference;
    // Devices browse/filter/reset opts in; header and homepage ranking is unchanged.
    if (sort === 'published-desc') {
      const dateA = publishedAt(a), dateB = publishedAt(b);
      if (dateA !== dateB) {
        if (dateA === null) return 1;
        if (dateB === null) return -1;
        return dateB - dateA;
      }
      const pathA = devicePath(a), pathB = devicePath(b);
      return pathA < pathB ? -1 : pathA > pathB ? 1 : 0;
    }
    const yearA = releaseYear(a), yearB = releaseYear(b);
    if (yearA !== yearB) {
      if (yearA === null) return 1;
      if (yearB === null) return -1;
      return yearB - yearA;
    }
    return String(a.title || '').localeCompare(String(b.title || ''), undefined, { numeric: true, sensitivity: 'base' });
  };
  const getDevices = () => devices.slice();
  const search = (query, options = {}) => {
    const value = normalize(query);
    const category = normalize(options.category), brand = normalize(options.brand);
    return devices.filter(device => {
      if (category && category !== 'all' && normalize(device.category) !== category) return false;
      if (brand && brand !== 'all' && normalize(device.brand) !== brand) return false;
      return !value || normalize([device.title, device.brand, device.category, device.releaseYear, aliasesText(device)].join(' ')).includes(value);
    }).sort((a, b) => compare(a, b, value, options.sort));
  };
  const fail = reason => { throw new Error(`Device search data is incomplete: ${reason}`); };
  const integer = (value, minimum = 0) => Number.isSafeInteger(value) && value >= minimum;

  const requestURL = page => {
    const current = new URL(window.location.href);
    const url = new URL(window.Shopify?.routes?.root || '/', current.origin);
    if (url.origin !== current.origin) fail('invalid storefront route');
    url.search = '';
    url.hash = '';
    url.searchParams.set('section_id', SECTION);
    url.searchParams.set('page', String(page));
    const preview = current.searchParams.get('preview_theme_id');
    if (preview) url.searchParams.set('preview_theme_id', preview);
    return url;
  };
  const validateDevice = (record, origin) => {
    if (!record || typeof record !== 'object' || Array.isArray(record) || typeof record.title !== 'string' || !record.title.trim() || typeof record.url !== 'string') fail('invalid device record');
    let destination;
    try { destination = new URL(record.url, origin); } catch (_) { fail('invalid device destination'); }
    if (destination.origin !== origin || !/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?collections\/[^/]+\/?$/i.test(destination.pathname)) fail('invalid device destination');
    for (const field of ['brand', 'category', 'image']) {
      if (record[field] != null && typeof record[field] !== 'string') fail(`invalid device ${field}`);
    }
    const aliases = record.aliases == null ? [] : record.aliases;
    if (typeof aliases !== 'string' && (!Array.isArray(aliases) || aliases.some(alias => typeof alias !== 'string'))) fail('invalid device aliases');
    if (record.image) {
      let image;
      try { image = new URL(record.image, origin); } catch (_) { fail('invalid device image'); }
      if (!['https:', 'http:'].includes(image.protocol)) fail('invalid device image');
    }
    return Object.freeze({
      title: record.title,
      url: destination.pathname + destination.search,
      category: record.category || '', brand: record.brand || '',
      aliases: Array.isArray(aliases) ? Object.freeze(aliases.slice()) : aliases,
      releaseYear: releaseYear(record), publishedAt: publishedAt(record), image: record.image || ''
    });
  };
  const validateStorefrontContext = data => {
    for (const key of ['theme', 'locale', 'country']) {
      if (typeof data[key] !== 'string' || !data[key]) fail(`missing ${key} context`);
    }
    const shopify = window.Shopify || {};
    const expected = { theme: shopify.theme?.id, locale: shopify.locale, country: shopify.country };
    for (const key of ['theme', 'locale', 'country']) {
      if (expected[key] != null && String(expected[key]) !== data[key]) fail(`unexpected ${key} context`);
    }
  };
  const validatePage = (data, page, context) => {
    if (!data || data.schema !== 1 || !integer(data.page, 1) || data.page !== page || !integer(data.pages, 1) || !integer(data.totalCollections) || !integer(data.pageSize, 1) || data.pageSize > 250 || !Array.isArray(data.devices)) fail('invalid page metadata');
    if (data.pages > MAX_PAGES || data.totalCollections > MAX_COLLECTIONS) fail('catalog exceeds supported pagination limits');
    const remaining = Math.max(0, data.totalCollections - (page - 1) * data.pageSize);
    if (data.pages !== Math.max(1, Math.ceil(data.totalCollections / data.pageSize)) || page > data.pages || data.devices.length > Math.min(data.pageSize, remaining)) fail('inconsistent page count');
    validateStorefrontContext(data);
    if (context) {
      for (const key of ['theme', 'locale', 'country', 'pages', 'totalCollections', 'pageSize']) {
        if (data[key] !== context[key]) fail(`changed ${key} context`);
      }
    }
    return data;
  };
  // Only the All Devices directory emits a complete server-rendered catalog.
  // Reuse it for every global search on that document instead of downloading it again.
  const adoptRenderedCatalog = () => {
    if (complete || pending || typeof document === 'undefined') return complete;
    const payload = document.querySelector('[data-sw-device-catalog]');
    if (!payload) return false;
    try {
      const data = JSON.parse(payload.textContent);
      if (!data || data.schema !== 1 || !integer(data.totalCollections) ||
          data.totalCollections > MAX_COLLECTIONS || !integer(data.scannedCollections) ||
          data.scannedCollections !== data.totalCollections || !Array.isArray(data.devices) ||
          data.devices.length > data.totalCollections) fail('incomplete rendered catalog');
      validateStorefrontContext(data);
      const origin = new URL(window.location.href).origin;
      const records = new Map();
      for (const record of data.devices) {
        const device = validateDevice(record, origin);
        const key = new URL(device.url, origin).pathname.replace(/\/$/, '');
        if (records.has(key)) fail('duplicate rendered device');
        records.set(key, device);
      }
      devices = Object.freeze(Array.from(records.values()).sort((a, b) => compare(a, b)));
      complete = true;
    } catch (_) {
      // A partial, stale-preview, or malformed seed must never become a complete list.
      // The normal paginated loader remains available as a recoverable fallback.
    }
    return complete;
  };
  const fetchPage = async (page, context) => {
    const url = requestURL(page);
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
    try {
      const response = await fetch(url.toString(), { credentials: 'same-origin', signal: controller.signal, headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      if (!response.ok) fail(`request failed (${response.status})`);
      if (response.url) {
        const actual = new URL(response.url, url);
        if (actual.origin !== url.origin || actual.pathname !== url.pathname) fail('unexpected response destination');
      }
      const html = await response.text();
      const payload = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-sw-device-page]');
      if (!payload) fail('missing page payload');
      return validatePage(JSON.parse(payload.textContent), page, context);
    } finally { clearTimeout(timer); }
  };
  const load = () => {
    adoptRenderedCatalog();
    if (complete) return Promise.resolve(getDevices());
    if (pending) return pending;
    pending = (async () => {
      const records = new Map();
      const visited = new Set();
      const origin = new URL(window.location.href).origin;
      let context = null;
      let page = 1;
      do {
        if (visited.has(page) || visited.size >= MAX_PAGES) fail('repeated or excessive page');
        const data = await fetchPage(page, context);
        if (!context) context = data;
        visited.add(data.page);
        for (const record of data.devices) {
          const device = validateDevice(record, origin);
          records.set(new URL(device.url, origin).pathname.replace(/\/$/, ''), device);
        }
        page += 1;
      } while (page <= context.pages);
      if (visited.size !== context.pages) fail('missing pages');
      devices = Object.freeze(Array.from(records.values()).sort((a, b) => compare(a, b)));
      complete = true;
      return getDevices();
    })().finally(() => { pending = null; });
    return pending;
  };

  adoptRenderedCatalog();
  window.SlickwrapsDeviceSearch = Object.freeze({ load, getDevices, isComplete: () => complete, normalize, search, score, compare });
})();
